<!-- eslint-disable vue/no-deprecated-dollar-listeners-api -->
<template>
  <div class="s-input__container"
       :class="$attrs.class"
       :style="$attrs.style"
  >
    <label
      class="s-input__label"
      :class="[{ 'text-gray-300': disabled || readonly }, hiddenLabel]"
      :aria-hidden="isScreenReaderActive ? true : undefined"
      :for="isScreenReaderActive ? undefined : inputId"
      :data-test="testId ? `label-${testId}` : null"
    >{{ labelText }}</label>

    <div class="position-relative">
      <sdui-icon
        v-if="leftIcon"
        class="s-input__left-icon position-absolute"
        :class="[leftIcon, { 'text-danger': isInvalid, 'text-gray-300': !isInvalid }]"
        :data-test="testId ? `icon-${testId}` : null"
      />
      <span
        ref="placeholder"
        class="s-input__placeholder"
        :class="{
          'd-none': !isPlaceholderVisible || isScreenReaderActive,
          'with-icon': leftIcon,
          lg: $attrs.size === 'lg',
          's-input__placeholder--is-ellipsized': placeholderIsSingleLine,
        }"
        :data-test="testId ? `placeholder-${testId}` : null"
        tabindex="-1"
        aria-hidden="true"
      ><span class="s-input__placeholder-inner-text" aria-hidden="true">{{ placeholder }}</span></span>

      <Field
        v-if="validationFieldName && validationRules"
        :validate-on-blur="doValidateOnBlur"
        :validate-on-input="doValidateOnInputChange"
        :label="validationFieldLabel"
        :name="validationFieldName"
        v-bind="filteredAttrsVeeValidate"
        v-slot="slotProps"
        :rules="validationRules"
        v-on="$listeners"
      >
        <b-form-input
          v-bind="{ ...slotProps.field, ...filteredAttrsVeeValidate }"
          :id="inputId"
          ref="input"
          :value="modelValue"
          class="s-input__input"
          :class="{ 's-input__input--has-left-icon': leftIcon, 's-input__input--invalid': isInvalid }"
          :placeholder="undefined"
          autocomplete="off"
          :data-test="testId ? `input-${testId}` : null"
          :disabled="disabled"
          :readonly="readonly"
          :aria-label="isScreenReaderActive ? localLabelText : undefined"
          :aria-invalid="isInvalid ? 'true' : undefined"
          :aria-describedby="descriptionId"
          v-on="$listeners"
        />
      </Field>
      <b-form-input
        v-else
        v-bind="filteredAttrs"
        :id="inputId"
        ref="input"
        :value="localModelValue"
        class="s-input__input"
        :class="{ 's-input__input--has-left-icon': leftIcon, 's-input__input--invalid': isInvalid }"
        :placeholder="undefined"
        autocomplete="off"
        :data-test="testId ? `input-${testId}` : null"
        :disabled="disabled"
        :readonly="readonly"
        :aria-label="isScreenReaderActive ? localLabelText : undefined"
        :aria-invalid="isInvalid ? 'true' : undefined"
        :aria-describedby="descriptionId"
        @input="(val) => (localModelValue = val)"
        v-on="$listeners"
      />

      <div v-if="$hasSlotContent($slots['right-action'])" class="s-input__right-action position-absolute">
        <slot name="right-action" />
      </div>
    </div>

    <!-- invalid text -->
    <p
      v-if="isInvalid"
      :id="`invalid-${inputId}`"
      class="s-input__invalid text-danger-dark nano mt-1 mb-0"
      :data-test="testId ? `invalid-${testId}` : null"
    >
      {{ invalidFieldMessage }}
    </p>

    <!-- help text -->
    <p
      v-if="helpText"
      :id="`help-${inputId}`"
      class="s-input__help text-gray-300 nano mt-1 mb-0"
      :data-test="testId ? `help-${testId}` : null"
    >
      {{ helpText }}
    </p>
  </div>
</template>

<script>
import { allIcons } from 'src/config/services/allIcons.js'
import { Field } from 'vee-validate'
import { useIsFieldDirty, useFieldError } from 'vee-validate'
import { onMounted, ref, watch } from 'vue'

export default {
  // TODO VUE3: This is a mix of Options API and Composition API which should be removed by refactoring
  // the whole component to Composition API. Right now it's needed to access validation state of VeeValidate.
  setup(props) {
    // no validation -> dont initialise handlers
    if (!props.validationFieldName || !props.validationRules) {
      return {
        doValidateOnBlur: undefined,
        doValidateOnInputChange: undefined,
        validationErrorMessage: undefined,
      }
    }

    const validationErrorMessage = ref(undefined)
    const doValidateOnBlur = ref(false)
    const doValidateOnInputChange = ref(false)

    onMounted(() => {
      const isFieldDirty = useIsFieldDirty(props.validationFieldName)
      const hasFieldError = useFieldError(props.validationFieldName)

      // OnBlur validation: Only when field is dirty
      watch(isFieldDirty, (newVal) => {
        doValidateOnBlur.value = newVal
      })

      // InputChange validation: Only when field already has error (and user changes the value again)
      watch(hasFieldError, (newVal) => {
        doValidateOnInputChange.value = !!newVal
        validationErrorMessage.value = newVal
      })
    })

    return {
      doValidateOnBlur,
      doValidateOnInputChange,
      validationErrorMessage,
    }
  },

  // TODO VUE3: Remove compatConfig when @vue/compat is removed
  compatConfig: {
    COMPONENT_V_MODEL: false,
  },

  // Prevent inheriting attributes from parent component to root element of this component.
  // This is to prevent having duplicate data-cy/test or aria attributes on the root element.
  inheritAttrs: false,

  name: 'SInput',

  components: { Field },

  emits: ['update:modelValue'],

  props: {
    modelValue: {
      type: [String, Number],
      default: '',
    },

    disabled: {
      type: Boolean,
      default: false,
    },

    readonly: {
      type: Boolean,
      default: false,
    },

    labelText: {
      type: String,
      default: '',
      required: true,
    },

    placeholder: {
      type: String,
      default: '',
    },

    placeholderIgnoredBySr: {
      type: Boolean,
      default: false,
    },

    placeholderIsSingleLine: {
      type: Boolean,
      default: false,
    },

    // TODO: Remove this prop after design is ready. We are not allowed to hide the label, because of accessibility
    hideLabel: {
      type: Boolean,
      default: false,
    },

    // Not required to use for field validation. Only use this for fields that are not part of a form and validate
    // special use cases.
    invalidText: {
      type: String,
      default: '',
    },

    helpText: {
      type: String,
      default: '',
    },

    leftIcon: {
      type: String,
      default: '',
      validator: function (value) {
        if (!value) return true
        const iconArr = value.split(' ')
        const validIcons = [...allIcons()]
        return iconArr.every((icon) => validIcons.includes(icon.replace('icon-', '')))
      },
    },

    testId: {
      type: String,
      default: '',
    },

    /*
    Value should match a string in "validations.attributes" (see [LANG].json)
    Setting this prop will also render attribute name="validationFieldName" on the input element.
    Must be unique within the <VForm> tag!
     */
    validationFieldName: {
      type: String,
      default: '',
    },

    /*
    Rules to validate, example:
      - validation-rules="required|email"
    See all available rules in src/config/setup/plugins/veeValidate/index.js
    */
    validationRules: {
      type: [String, Object],
      default: '',
    },

    /*
    validationFieldName must be unique within the <VForm> tag. If that is not possible or if you need to translate
    a different attribute name, you can set this prop to a different value that exists in "validations.attributes".
     */
    validationFieldLabel: {
      type: String,
      default: '',
    },
  },

  data: () => ({
    inputId: null,
    localLabelText: '',
    isPlaceholderVisible: true,
    isScreenReaderActive: false,
  }),

  computed: {
    localModelValue: {
      get() {
        return this.modelValue
      },

      set(value) {
        this.$emit('update:modelValue', value)
      },
    },

    filteredAttrs() {
      const { class: _cls, style: _style, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    filteredAttrsVeeValidate() {
      const { class: _cls, style: _style, name: _name, ...otherAttrs } = this.$attrs
      return otherAttrs
    },

    invalidFieldMessage() {
      return this.invalidText || this.validationErrorMessage
    },

    isInvalid() {
      return !!this.invalidText || !!this.validationErrorMessage
    },

    descriptionId() {
      // is invalid and has help text
      if (this.isInvalid && this.helpText) return `invalid-${this.inputId} help-${this.inputId}`

      // is invalid, but no help text
      if (this.isInvalid) return `invalid-${this.inputId}`

      // is valid and has help text
      if (this.helpText) return `help-${this.inputId}`

      // is valid and no help text
      return undefined
    },

    hiddenLabel() {
      return this.hideLabel ? 'sr-only' : ''
    },
  },

  methods: {
    toggleScreenReaderLabel() {
      if (!this.isScreenReaderActive) return
      // We need to hide the placeholder from screen readers by setting aria-hidden="true".
      // This is because our placeholders have mostly the same value as the label and a screen reader would read
      // it twice, confusing the user.
      // If placeholder and labelText are the same, we can just use the labelText as the label value.
      // If they are not the same, we concatenate them with a comma and use that as the label value.
      const isSameLabelPlaceholder = this.labelText === this.placeholder || !this.placeholder || this.placeholderIgnoredBySr
      this.localLabelText = isSameLabelPlaceholder ? this.labelText : `${this.labelText}, ${this.placeholder}`
    },
  },

  watch: {
    '$store.state.isScreenReaderActive'(newVal) {
      this.isScreenReaderActive = newVal
      this.toggleScreenReaderLabel()
    },

    modelValue: {
      async handler(newVal, oldVal) {
        if (oldVal === newVal) return
        // eslint-disable-next-line
        this.isPlaceholderVisible = !!newVal ? !String(newVal)?.length : true;
      },

      immediate: true,
    },

    placeholder: {
      async handler() {
        this.toggleScreenReaderLabel()
      },
    }
  },

  async beforeMount() {
    this.isScreenReaderActive = import.meta.env.IS_STORYBOOK ? false : this.$store.state.isScreenReaderActive
    this.toggleScreenReaderLabel()
  },

  created() {
    this.inputId = this.$uuid()
  },
}
</script>

<style scoped lang="scss">
.s-input__label {
  font-size: pxtorem(15);
}

.s-input__placeholder {
  position: absolute;
  z-index: 1;
  left: 12px;
  color: $color-gray-300;
  pointer-events: none;
  height: 100%;
  display: flex;
  align-items: center;

  &.s-input__placeholder--is-ellipsized  {
    right: 12px;
  }

  &.with-icon {
    left: 41px;
  }

  &.lg {
    left: 13px;

    &.with-icon {
      left: 41px;
    }
  }
}

.s-input__placeholder-inner-text {
  display: inline-flex;

  .s-input__placeholder--is-ellipsized & {
    display: block;
    height: auto;
    position: relative;

    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;

    top: 0;
  }
}

.s-input__input {
  // bootstrap overwrites
  box-shadow: 0px 2px 4px rgba($color-black-rgb, 0.08);
  border-radius: 8px;
}

.s-input__input[type='range'] {
  box-shadow: none;
}

.s-input__input:disabled,
.s-input__input[readonly] {
  color: $color-gray-300;
  background-color: $color-white;
  box-shadow: none;
}

.s-input__input--invalid {
  border-color: $color-danger;
  background-color: $color-danger-light;

  &:hover {
    border-color: $color-danger;
  }
}

.s-input__input--has-left-icon {
  padding-left: 40px;
}

.s-input__left-icon {
  top: 50%;
  transform: translateY(-50%);
  left: 8px;
}

.s-input__right-action {
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
}

.s-input__input:read-only {
  cursor: not-allowed;
}

// if inside a b-input-group, we need some extra css
.input-group .s-input__container {
  flex: 1 1 auto;
}

.input-group .s-input__container:not(:last-child) .s-input__input {
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
}

.input-group .s-input__container:not(:first-child) .s-input__input {
  border-bottom-left-radius: 0;
  border-top-left-radius: 0;
}
</style>
